import { addDays, endOfDay, startOfDay } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { debounce } from "../../../utils/lodash-replacements";
import { useActiveFBOs } from "../../../containers/ActiveFBOContainer";
import { useApi } from "../../../containers/ApiContainer";
import { useHangarsState } from "../../../containers/HangarsStateContainer";
import { useRampsState } from "../../../containers/RampsStateContainer";
import { TripService } from "../../../services/TripService";
import { CsrStatus, TripWithTenant } from "../../../types";
import { isInfinityDate } from "../../../utils/dateHelpers";
import { isTripPast } from "../../../utils/schedulingHelpers";
import { floorToQuarterHour } from "../../../utils/timeUtils";
import { ScheduleDashboardPresenter } from "./ScheduleDashboardPresenter";

interface TripWithPlacement extends TripWithTenant {
  hangar_id?: string | null;
  ramp_id?: string | null;
  hangar_name?: string | null;
  ramp_name?: string | null;
  location_name?: string | null;
  location_type?: string | null;
}

export const ScheduleDashboard: React.FC = () => {
  const { activeFBO } = useActiveFBOs();
  const { postgrest } = useApi();
  const { hangars } = useHangarsState();
  const { ramps } = useRampsState();

  // Current time state
  const [currentTime, setCurrentTime] = useState<Date>(() => floorToQuarterHour(new Date()));

  // Visible date range state - default to current date -7 days to +30 days
  const [visibleStartDate, setVisibleStartDate] = useState<Date>(() =>
    startOfDay(addDays(new Date(), -7))
  );
  const [visibleEndDate, setVisibleEndDate] = useState<Date>(() =>
    endOfDay(addDays(new Date(), 30))
  );

  const [trips, setTrips] = useState<TripWithPlacement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState<TripWithPlacement | null>(null);
  const [tripDetailsOpen, setTripDetailsOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"table" | "timeline">(() => {
    const saved = localStorage.getItem("scheduleViewMode");
    return saved === "table" || saved === "timeline" ? saved : "timeline";
  });

  // Show past trips filter - persisted to localStorage
  const [showPastTrips, setShowPastTrips] = useState<boolean>(() => {
    const saved = localStorage.getItem("schedule_showPastTrips");
    return saved === "true"; // defaults to false if not set
  });

  // Persist showPastTrips to localStorage when changed
  useEffect(() => {
    localStorage.setItem("schedule_showPastTrips", String(showPastTrips));
  }, [showPastTrips]);

  // Track if timeline is scrolled away from initial position
  const [isTimelineScrolledAway, setIsTimelineScrolledAway] = useState(false);
  const [shouldResetScroll, setShouldResetScroll] = useState(false);

  // Initialize services
  const tripService = useMemo(() => new TripService(postgrest), [postgrest]);

  // Filter states
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [tailNumberFilter, setTailNumberFilter] = useState<string>("");

  // Load trips for the current FBO
  const loadTrips = async () => {
    if (!activeFBO?.id) return;

    setLoading(true);
    setError(null);

    try {
      // Load trips with tenant and placement info
      const allTrips = await tripService.listTripsWithTenantAndPlacement(activeFBO.id);

      // Rename trip_id to id for consistency
      const tripsWithId = allTrips.map(trip => ({
        ...trip,
        id: trip.trip_id
      }));

      setTrips(tripsWithId);
    } catch (err) {
      console.error("Failed to load trips:", err);
      setError("Failed to load trips. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle current time changes
  const handleTimeChange = useCallback((newTime: Date) => {
    setCurrentTime(newTime);
  }, []);

  // Handle Now button click - reset time and scroll
  const handleNowClick = useCallback(() => {
    const now = floorToQuarterHour(new Date());
    setCurrentTime(now);

    // Update visible date range to center on "now"
    setVisibleStartDate(startOfDay(addDays(now, -7)));
    setVisibleEndDate(endOfDay(addDays(now, 30)));

    setShouldResetScroll(true);
    setIsTimelineScrolledAway(false);
  }, []);

  // Handle timeline scroll changes
  const handleTimelineScrollChange = useCallback((scrolledAway: boolean) => {
    setIsTimelineScrolledAway(scrolledAway);
  }, []);

  // Handle scroll reset acknowledgment from timeline
  const handleScrollReset = useCallback(() => {
    setShouldResetScroll(false);
  }, []);

  // Create debounced update function for visible date range
  const debouncedTimeRangeUpdate = useMemo(
    () => debounce((start: Date, end: Date) => {
      setVisibleStartDate(start);
      setVisibleEndDate(end);
    }, 100), // 100ms debounce to prevent excessive updates during dragging
    []
  );

  // Handle timeline viewport changes
  const handleTimeRangeChange = useCallback(
    (newStartDate: Date, newEndDate: Date) => {
      debouncedTimeRangeUpdate(newStartDate, newEndDate);
    },
    [debouncedTimeRangeUpdate]
  );

  useEffect(() => {
    loadTrips();
  }, [activeFBO?.id]);

  // Adjust visible date range when location filter changes
  useEffect(() => {
    if (selectedLocationIds.length === 0) {
      // Reset to default range when no filter is applied
      const now = new Date();
      setVisibleStartDate(startOfDay(addDays(now, -7)));
      setVisibleEndDate(endOfDay(addDays(now, 30)));
      return;
    }

    // Filter trips by selected locations
    const locationFilteredTrips = trips.filter((trip) =>
      selectedLocationIds.some((locationId) =>
        trip.hangar_id === locationId || trip.ramp_id === locationId
      )
    );

    if (locationFilteredTrips.length === 0) {
      // No trips found in selected locations, keep current range
      return;
    }

    // Find the date range of filtered trips
    let earliestDate: Date | null = null;
    let latestDate: Date | null = null;

    locationFilteredTrips.forEach((trip) => {
      const arrivalDate = new Date(trip.arrival_date);
      const departureDate = new Date(trip.departure_date);

      if (!earliestDate || arrivalDate < earliestDate) {
        earliestDate = arrivalDate;
      }
      if (!latestDate || departureDate > latestDate) {
        latestDate = departureDate;
      }
    });

    if (earliestDate && latestDate) {
      // Add padding: 3 days before earliest and 3 days after latest
      const paddedStart = startOfDay(addDays(earliestDate, -3));
      const paddedEnd = endOfDay(addDays(latestDate, 3));

      setVisibleStartDate(paddedStart);
      setVisibleEndDate(paddedEnd);
    }
  }, [selectedLocationIds, trips]);


  // Combine hangars and ramps into location list with trip counts
  const locations = useMemo(() => {
    const locationMap = new Map<string, { id: string; name: string; type: "hangar" | "ramp"; tripCount: number }>();

    // Add hangars
    if (hangars) {
      hangars.forEach((hangar) => {
        locationMap.set(hangar.id, {
          id: hangar.id,
          name: hangar.name,
          type: "hangar",
          tripCount: 0,
        });
      });
    }

    // Add ramps
    if (ramps) {
      ramps.forEach((ramp) => {
        locationMap.set(ramp.id, {
          id: ramp.id,
          name: ramp.name,
          type: "ramp",
          tripCount: 0,
        });
      });
    }

    // Count trips per location based on their placement data
    trips.forEach((trip) => {
      if (trip.hangar_id) {
        const location = locationMap.get(trip.hangar_id);
        if (location) {
          location.tripCount++;
        }
      }
      if (trip.ramp_id) {
        const location = locationMap.get(trip.ramp_id);
        if (location) {
          location.tripCount++;
        }
      }
    });

    return Array.from(locationMap.values());
  }, [hangars, ramps, trips]);

  // Filter trips by visible date range, selected locations, status, and tail number
  const filteredTrips = useMemo(() => {
    return trips.filter((trip) => {
      // Filter by date range - include trips that overlap with the visible range
      const tripStart = new Date(trip.arrival_date);
      // Handle infinity departure (base tenants never end)
      const tripEnd = isInfinityDate(trip.departure_date)
        ? new Date('2099-12-31') // Far future for comparison
        : new Date(trip.departure_date);
      const dateMatch = tripStart <= visibleEndDate && tripEnd >= visibleStartDate;

      if (!dateMatch) return false;

      // Filter by location if any are selected
      if (selectedLocationIds.length > 0) {
        const locationMatch = selectedLocationIds.some((locationId) =>
          trip.hangar_id === locationId || trip.ramp_id === locationId
        );
        if (!locationMatch) return false;
      }

      // Filter by CSR status
      if (statusFilter !== "all" && (trip.csr_status || "requested") !== statusFilter) {
        return false;
      }

      // Filter out past trips (where departure is before current time) unless showPastTrips is enabled
      if (!showPastTrips && isTripPast(trip, currentTime)) {
        return false;
      }

      // Filter by tail number
      if (tailNumberFilter && trip.tail_number) {
        const matchesTailNumber = trip.tail_number
          .toLowerCase()
          .includes(tailNumberFilter.toLowerCase());
        if (!matchesTailNumber) return false;
      }

      return true;
    });
  }, [trips, visibleStartDate, visibleEndDate, selectedLocationIds, statusFilter, tailNumberFilter, showPastTrips, currentTime]);

  // Handle trip creation success
  const handleTripCreated = () => {
    setCreateDialogOpen(false);
    loadTrips();
  };

  // Handle trip edit
  const handleTripEdit = useCallback((trip: TripWithPlacement) => {
    setSelectedTrip(trip);
    setTripDetailsOpen(true);
  }, []);

  // Handle trip details close
  const handleTripDetailsClose = useCallback(() => {
    setTripDetailsOpen(false);
    setSelectedTrip(null);
  }, []);

  // Handle bulk upload completion
  const handleBulkUploadComplete = useCallback((_: number) => {
    setBulkUploadOpen(false);
    loadTrips(); // Reload trips after bulk upload
  }, []);

  // Optimistic update for CSR status changes - updates local state immediately
  // This avoids a full table refresh when changing status in the table
  const handleCsrStatusChange = useCallback((tripId: string, newStatus: CsrStatus) => {
    setTrips(prevTrips =>
      prevTrips.map(trip =>
        trip.trip_id === tripId
          ? { ...trip, csr_status: newStatus }
          : trip
      )
    );
  }, []);

  // Handle view mode change
  const handleViewModeChange = (
    _: React.MouseEvent<HTMLElement>,
    newMode: "table" | "timeline" | null
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
      localStorage.setItem("scheduleViewMode", newMode);
    }
  };

  return (
    <ScheduleDashboardPresenter
      activeFBO={activeFBO}
      currentTime={currentTime}
      handleTimeChange={handleTimeChange}
      handleNowClick={handleNowClick}
      isTimelineScrolledAway={isTimelineScrolledAway}
      startDate={visibleStartDate}
      endDate={visibleEndDate}
      trips={trips}
      filteredTrips={filteredTrips}
      loading={loading}
      error={error}
      createDialogOpen={createDialogOpen}
      setCreateDialogOpen={setCreateDialogOpen}
      bulkUploadOpen={bulkUploadOpen}
      setBulkUploadOpen={setBulkUploadOpen}
      tripDetailsOpen={tripDetailsOpen}
      selectedTrip={selectedTrip}
      viewMode={viewMode}
      handleViewModeChange={handleViewModeChange}
      loadTrips={loadTrips}
      handleTripCreated={handleTripCreated}
      handleTripEdit={handleTripEdit}
      handleTripDetailsClose={handleTripDetailsClose}
      handleBulkUploadComplete={handleBulkUploadComplete}
      handleTimeRangeChange={handleTimeRangeChange}
      shouldResetScroll={shouldResetScroll}
      handleScrollReset={handleScrollReset}
      handleTimelineScrollChange={handleTimelineScrollChange}
      locations={locations}
      selectedLocationIds={selectedLocationIds}
      setSelectedLocationIds={setSelectedLocationIds}
      statusFilter={statusFilter}
      setStatusFilter={setStatusFilter}
      tailNumberFilter={tailNumberFilter}
      setTailNumberFilter={setTailNumberFilter}
      showPastTrips={showPastTrips}
      setShowPastTrips={setShowPastTrips}
      onCsrStatusChange={handleCsrStatusChange}
    />
  );
};